import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { CampaignState } from 'constants/campaignStates';
import { PUBLISH_CAMPAIGN_MODAL } from 'modules/workflows/constants';
import { openModal } from 'modules/workflows/ducks';
import { Button, ButtonProps, BUTTON_SIZES } from 'ui/button';
import { LogoColors } from 'ui/logo';
import { Spinner } from 'ui/spinner';
import noop from 'utils/noop';

import { fetchCampaignState } from './CampaignButton.gql';
import { getButtonProps } from './CampaignButton.utils';

interface CampaignButtonProps extends ButtonProps {
  campaignId: number;
  isReadOnly: boolean;
  isDisabled: boolean;
  setIsReadOnly: (val: boolean) => void;
  onCancel: () => void;
}

const CampaignButton = ({
  campaignId,
  isReadOnly,
  isDisabled,
  onCancel = noop,
  onClick = noop,
  setIsReadOnly = noop,
  ...props
}: CampaignButtonProps) => {
  const dispatch = useDispatch();
  const [spinnerIsShown, setSpinnerIsShown] = useState(false);

  const showPublishModal = () => {
    dispatch(openModal(PUBLISH_CAMPAIGN_MODAL, { onCancel, onPublish: onClick }));
  };

  const [checkStateThenPublish] = useLazyQuery(fetchCampaignState, {
    fetchPolicy: 'cache-and-network',
    variables: { campaignId },
    onCompleted: (resp) => {
      if (resp?.campaign?.campaignState === CampaignState.Running) {
        showPublishModal();
      } else {
        // @ts-ignore
        onClick();
      }
      setSpinnerIsShown(false);
    },
    onError: () => {
      // For safety, show the modal as a default if the operation fails
      showPublishModal();
      setSpinnerIsShown(false);
    },
  });

  const handlePublish = () => {
    setSpinnerIsShown(true);
    checkStateThenPublish();
  };

  const buttonDetails = getButtonProps(
    isReadOnly,
    campaignId,
    isDisabled,
    setIsReadOnly,
    handlePublish,
  );
  const buttonProps = { onClick, ...props, ...buttonDetails.props };

  return (
    <Button {...buttonProps} size={BUTTON_SIZES.LARGE}>
      {buttonDetails.text}
      {spinnerIsShown && (
        <>
          &nbsp;&nbsp; <Spinner color={LogoColors.WHITE} size={16} />
        </>
      )}
    </Button>
  );
};

export default CampaignButton;
